import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { shallowEqual } from "react-redux";
import { Card, Spin } from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { format, parseISO, getDay } from "date-fns";
import API from "../../../../app/helpers/devApi";
import "./AttendanceWidget.css";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const statusLabels = {
  present: "Present",
  absent: "Absent",
  leave: "On Leave",
  holiday: "Holiday",
  weekend: "Weekend",
  wfh: "WFH",
  late: "Late",
};

function isFutureDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return false;
  const today = format(new Date(), "yyyy-MM-dd");
  return dateStr > today;
}

/** Compute card counts from raw API attendances using daily_status / shift_day_status */
function computeStatsFromAttendances(attendances) {
  const statsCount = {
    present: 0,
    absent: 0,
    leave: 0,
    late: 0,
    weekend: 0,
    holiday: 0,
  };
  if (!Array.isArray(attendances)) return statsCount;

  for (const att of attendances) {
    const status = (att.daily_status || "").trim();
    const shiftStatus = (att.shift_day_status || "").trim();
    const dayIs = att.day_is || att.date || "";
    const isFutureDateVal = isFutureDate(dayIs);

    if (status === "P" || status === "WP" || status === "HP" || status === "L" || status === "EO" || status === "L-EO" || status === "WFH" || status === "HLv") {
      statsCount.present++;
    }
    if (status === "A") {
      if (!isFutureDateVal) {
        statsCount.absent++;
      }
    }
    if (status === "Lv" || status === "HLv") {
      statsCount.leave++;
    }
    if (status === "L" || status === "L-EO") {
      statsCount.late++;
    }
    if (shiftStatus === "W") {
      statsCount.weekend++;
    }
    if (shiftStatus === "H") {
      statsCount.holiday++;
    }
  }

  return statsCount;
}

function mapApiItemToDisplay(att) {
  const dateStr = att.day_is || att.attendance_date || att.date;
  let dateNum = 0;
  if (dateStr && typeof dateStr === "string" && dateStr.length >= 10) {
    try {
      const d = parseISO(dateStr);
      dateNum = parseInt(format(d, "d"), 10);
    } catch (_) {}
  }
  let dayIndex = 0;
  if (dateStr && typeof dateStr === "string" && dateStr.length >= 10) {
    try {
      dayIndex = getDay(parseISO(dateStr));
    } catch (_) {}
  }
  const day = att.day_name || DAY_NAMES[dayIndex] || "—";

  let status = "present";
  const shiftStatus = (att.shift_day_status || att.day_status || "").toUpperCase();
  const dailyStatus = (att.daily_status || att.status || "").toUpperCase();

  if (shiftStatus === "W") status = "weekend";
  else if (shiftStatus === "H") status = "holiday";
  else if (dailyStatus === "WFH") status = "wfh";
  else if (dailyStatus === "LV" || dailyStatus === "Lv" || dailyStatus === "LEAVE" || dailyStatus === "HLv") status = "leave";
  else if (dailyStatus === "A" || dailyStatus === "ABSENT" || dailyStatus === "AB") status = "absent";
  else if (dailyStatus === "L" || dailyStatus === "L-EO") status = "late";
  else if (shiftStatus === "R" || dailyStatus === "P" || dailyStatus === "PRESENT" || dailyStatus === "WP" || dailyStatus === "HP" || dailyStatus === "EO" || !shiftStatus) status = "present";

  const checkIn = att.in_time || att.check_in_time || att.check_in || att.shift_start_time || "";
  const checkOut = att.out_time || att.check_out_time || att.check_out || att.shift_end_time || "";

  return {
    date: dateNum,
    day,
    status,
    checkIn: checkIn ? formatTime(checkIn) : null,
    checkOut: checkOut ? formatTime(checkOut) : null,
  };
}

function formatTime(val) {
  if (val == null || val === "") return null;
  if (typeof val === "string" && /^\d{2}:\d{2}/.test(val)) return val.substring(0, 5);
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
    try {
      return format(parseISO(val), "HH:mm");
    } catch (_) {}
  }
  if (typeof val === "string") return val;
  return String(val);
}

function StatusIcon({ status }) {
  switch (status) {
    case "present":
    case "wfh":
    case "holiday":
    case "weekend":
      return <CheckCircleOutlined />;
    case "absent":
      return <CloseCircleOutlined />;
    case "leave":
    case "late":
      return <ExclamationCircleOutlined />;
    default:
      return null;
  }
}

export function AttendanceWidget() {
  const {
    auth,
  } = useSelector(
    ({ auth }) => ({
      auth: auth.coreModuleInfo,
    }),
    shallowEqual
  );

  const [attendanceData, setAttendanceData] = useState([]);
  const [rawAttendances, setRawAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState("");

  const employeeId = auth?.employee_id;

  useEffect(() => {
    if (!employeeId) {
      setAttendanceData([]);
      setRawAttendances([]);
      setCurrentMonth(format(new Date(), "MMMM yyyy"));
      setLoading(false);
      return;
    }

    const start = new Date();
    start.setDate(1);
    const startDate = format(start, "yyyy-MM-dd");
    const endDate = format(new Date(), "yyyy-MM-dd");

    setCurrentMonth(format(start, "MMMM yyyy"));

    const fetchAttendance = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await API.get("/attendify/employee-attendance/filtered", {
          params: {
            employee_ids: employeeId,
            start_date: startDate,
            end_date: endDate,
            page: 1,
            limit: 31,
          },
        });

        const employees = response?.data?.data?.data ?? [];
        console.log('employess',employees)
        const list = Array.isArray(employees) ? employees : [];
        const employee = list.find((e) => Number(e.id) === Number(employeeId)) || list[0];
        const rawAttendances = employee?.attendances ?? [];
        const mapped = rawAttendances.map(mapApiItemToDisplay).sort((a, b) => (a.date || 0) - (b.date || 0));
        setAttendanceData(mapped);
        setRawAttendances(rawAttendances);
      } catch (err) {
        setError(err?.message || "Failed to load attendance");
        setAttendanceData([]);
        setRawAttendances([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [employeeId]);

  const stats = computeStatsFromAttendances(rawAttendances);

  if (loading && attendanceData.length === 0) {
    return (
      <div className="attendance-widget">
        <Card className="attendance-widget__card">
          <div className="attendance-widget__loading">
            <Spin tip="Loading attendance..." />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="attendance-widget">
      <div className="attendance-widget__card">
        <div className="attendance-widget__header">
          <h3 className="attendance-widget__title">
          </h3>
          <span className="attendance-widget__month-badge">{currentMonth || format(new Date(), "MMMM yyyy")}</span>
        </div>
        <div className="attendance-widget__content">
          {error && (
            <div className="attendance-widget__error">{error}</div>
          )}
          {/* Stats Summary */}
          <div className="attendance-widget__stats">
            <div className="attendance-widget__stat-card attendance-widget__stat-present">
              <p className="attendance-widget__stat-value">{stats.present}</p>
              <p className="attendance-widget__stat-label">Present</p>
            </div>
            <div className="attendance-widget__stat-card attendance-widget__stat-absent">
              <p className="attendance-widget__stat-value">{stats.absent}</p>
              <p className="attendance-widget__stat-label">Absent</p>
            </div>
            <div className="attendance-widget__stat-card attendance-widget__stat-leave">
              <p className="attendance-widget__stat-value">{stats.leave}</p>
              <p className="attendance-widget__stat-label">Leave</p>
            </div>
          </div>

          {/* Attendance List */}
          <div className="attendance-widget__list">
            {attendanceData.length === 0 && !error ? (
              <p className="attendance-widget__empty">No attendance data for this month.</p>
            ) : (
              attendanceData.map((day, idx) => (
                <div
                  key={`att-${day.date}-${day.day}-${idx}`}
                  className={`attendance-widget__list-item attendance-widget__row-${day.status}`}
                >
                  <div className="attendance-widget__list-item-left">
                    <div className="attendance-widget__day-cell">
                      <p className="attendance-widget__day-num">{day.date}</p>
                      <p className="attendance-widget__day-name">{day.day}</p>
                    </div>
                    <div className="attendance-widget__status-wrap">
                      <StatusIcon status={day.status} />
                      <span>{statusLabels[day.status] || day.status}</span>
                    </div>
                  </div>
                  {day.checkIn && (
                    <div className="attendance-widget__list-item-right">
                      <p className="attendance-widget__time">
                        {day.checkIn} - {day.checkOut}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
